import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Bot, Check, X, Users } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useOrganizationStore } from '../stores/organizationStore';
import { organizationApi } from '../services/api';
import { Button, Input, Modal, WeeklyDaySelector, ErrorDisplay } from '../components/common';
import { showToast } from '../components/common/Toast';
import { Header } from '../components/layout/Header';
import { useTranslation } from 'react-i18next';
import type { OrganizationMember } from '@taskfly/shared/types';

interface UserInList {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  telegramChatId?: string;
  kioskPin?: string;
  workShifts?: { startTime: string; endTime: string }[];
  workScheduleDays?: number[];
  createdAt: string;
}

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, isLoading: authLoading } = useAuthStore();
  const { t } = useTranslation();
  const { activeOrganizationId } = useOrganizationStore();
  const [users, setUsers] = useState<UserInList[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserInList | null>(null);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserInList | null>(null);
  const [telegramBotToken, setTelegramBotToken] = useState('');
  const [isLinkingBot, setIsLinkingBot] = useState(false);
  const [linkMessage, setLinkMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    kioskPin: '',
    workShifts: [{ startTime: '09:00', endTime: '18:00' }] as { startTime: string; endTime: string }[],
    workScheduleDays: [1, 2, 3, 4, 5],
  });

  const fetchUsers = async () => {
    setFetchError(null);
    try {
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.data.users || data.data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setFetchError(t('users.error.loadMembers'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrgMembers = async () => {
    if (!activeOrganizationId) return;
    try {
      const res = await organizationApi.getMembers(activeOrganizationId);
      if (res.success && res.data) {
        setOrgMembers(res.data as OrganizationMember[]);
      }
    } catch (error) {
      console.error('Failed to fetch org members:', error);
      showToast(t('users.error.loadMembers'), 'error');
    }
  };

  useEffect(() => {
    // Wait for auth check to complete
    if (authLoading) {
      return;
    }

    const activeOrgRole = currentUser?.organizationRoles?.find(r => r.orgId === activeOrganizationId)?.role;
    if (!currentUser || activeOrgRole !== 'ADMIN') {
      navigate('/');
      return;
    }
    fetchUsers();
    fetchOrgMembers();
  }, [currentUser, navigate, authLoading, activeOrganizationId]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
      const method = editingUser ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        await fetchUsers();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to save user:', error);
      showToast(t('users.error.saveUser'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;
    const userId = showDeleteConfirm;
    setShowDeleteConfirm(null);
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (response.ok) {
        await fetchUsers();
        showToast(t('users.success.userDeleted'), 'success');
      } else {
        showToast(t('users.error.deleteUser'), 'error');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      showToast(t('users.error.deleteUser'), 'error');
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'ADMIN' | 'MEMBER') => {
    if (!activeOrganizationId) return;
    const previousRole = orgMembers.find(m => m.userId === userId)?.role;
    try {
      const res = await organizationApi.updateMemberRole(activeOrganizationId, userId, newRole);
      if (res.success) {
        const { checkAuth } = useAuthStore.getState();
        await checkAuth();
        await fetchOrgMembers();
      } else {
        // Revert on API failure
        setOrgMembers(prev => prev.map(m => m.userId === userId ? { ...m, role: (previousRole || 'MEMBER') as 'ADMIN' | 'MEMBER' } : m));
        showToast(t('users.error.updateRole'), 'error');
      }
    } catch (error) {
      console.error('Failed to update role:', error);
      // Revert on network failure
      setOrgMembers(prev => prev.map(m => m.userId === userId ? { ...m, role: (previousRole || 'MEMBER') as 'ADMIN' | 'MEMBER' } : m));
      showToast(t('users.error.updateRole'), 'error');
    }
  };

  const getRole = (userId: string): 'ADMIN' | 'MEMBER' | undefined => {
    return orgMembers.find(m => m.userId === userId)?.role;
  };

  const handleLinkTelegram = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLinkingBot(true);
    setLinkMessage(null);

    try {
      // Send bot token to backend to validate and store
      const response = await fetch('/api/users/telegram/setup-bot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ 
          botToken: telegramBotToken,
          userId: selectedUser?.id 
        }),
      });

      const data = await response.json();
      if (data.success) {
        setLinkMessage({ type: 'success', text: t('users.page.botConfigured') });
        await fetchUsers();
        setTimeout(() => {
          setShowTelegramModal(false);
          setTelegramBotToken('');
          setLinkMessage(null);
        }, 2000);
      } else {
        setLinkMessage({ type: 'error', text: data.error?.message || t('users.error.configureBot') });
      }
    } catch (error) {
      setLinkMessage({ type: 'error', text: t('users.error.configureBot') });
    } finally {
      setIsLinkingBot(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      kioskPin: '',
      workShifts: [{ startTime: '09:00', endTime: '18:00' }],
      workScheduleDays: [1, 2, 3, 4, 5],
    });
    setEditingUser(null);
  };

  const openEditModal = (user: UserInList) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      kioskPin: user.kioskPin || '',
      workShifts: user.workShifts && user.workShifts.length > 0
        ? user.workShifts
        : [{ startTime: '09:00', endTime: '18:00' }],
      workScheduleDays: user.workScheduleDays || [1, 2, 3, 4, 5],
    });
    setShowModal(true);
  };

  const openTelegramModal = (user: UserInList) => {
    setSelectedUser(user);
    setTelegramBotToken('');
    setLinkMessage(null);
    setShowTelegramModal(true);
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <Header title={t('users.title')} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-gray-500">{t('common.loading')}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title={t('users.title')} />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {fetchError && (
          <div className="mb-4">
            <ErrorDisplay message={fetchError} variant="error" dismissible onDismiss={() => setFetchError(null)} />
            <Button variant="secondary" onClick={fetchUsers} className="mt-2">{t('users.retry')}</Button>
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            {t('users.page.addUser')}
          </Button>
        </div>

        {/* Card view for mobile screens */}
        <div className="sm:hidden space-y-3">
          {users.map((user) => (
            <div key={user.id} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-medium">
                      {user.firstName[0]}{user.lastName[0]}
                    </span>
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {user.email}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between gap-3 py-2 border-t border-gray-100 dark:border-gray-800">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t('users.role')}</label>
                  {getRole(user.id) ? (
                    <select
                      value={getRole(user.id)!}
                      onChange={(e) => handleRoleChange(user.id, e.target.value as 'ADMIN' | 'MEMBER')}
                      className="text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 w-full min-h-[44px]"
                      disabled={user.id === currentUser?.id}
                    >
                      <option value="MEMBER">{t('users.member')}</option>
                      <option value="ADMIN">{t('users.admin')}</option>
                    </select>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('users.telegram')}</p>
                  {user.telegramChatId ? (
                    <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                      <Check className="w-4 h-4" /> {t('users.connected')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-gray-400">
                      <X className="w-4 h-4" /> {t('common.no')}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800 mt-2">
                <button
                  onClick={() => openTelegramModal(user)}
                  className="p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-blue-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={t('users.page.configureBot')}
                >
                  <Bot className="w-5 h-5" />
                </button>
                <button
                  onClick={() => openEditModal(user)}
                  className="p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={t('users.page.editUser')}
                >
                  <Edit className="w-5 h-5" />
                </button>
                {user.id !== currentUser?.id && (
                  <button
                    onClick={() => setShowDeleteConfirm(user.id)}
                    className="p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-red-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('users.page.deleteUser')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
              <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                {t('users.noUsersFound')}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('users.addFirstUserSubtitle')}
              </p>
              <button
                onClick={() => { setEditingUser(null); resetForm(); setShowModal(true); }}
                className="px-4 py-2 min-h-[44px] bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('users.addFirstUser')}
              </button>
            </div>
          )}
        </div>
        
        {/* Table view for desktop screens */}
        {users.length > 0 ? (
        <div className="hidden sm:block bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          {/* Responsive table wrapper with horizontal scroll */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.table.name')}
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.email')}
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.role')}
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.telegram')}
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.table.actions')}
                  </th>
                </tr>
              </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
                        {user.firstName[0]}{user.lastName[0]}
                      </span>
                    </div>
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                </td>
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <span className="truncate block max-w-[200px]">{user.email}</span>
                </td>
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                  {getRole(user.id) ? (
                    <select
                      value={getRole(user.id)!}
                      onChange={(e) => handleRoleChange(user.id, e.target.value as 'ADMIN' | 'MEMBER')}
                      className="text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1 min-h-[44px] min-w-[100px]"
                      disabled={user.id === currentUser?.id}
                    >
                      <option value="MEMBER">{t('users.member')}</option>
                      <option value="ADMIN">{t('users.admin')}</option>
                    </select>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                  {user.telegramChatId ? (
                    <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                      <Check className="w-4 h-4" /> {t('users.connected')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <X className="w-4 h-4" /> {t('users.notConnected')}
                    </span>
                  )}
                </td>
                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm">
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    <button
                      onClick={() => openTelegramModal(user)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-blue-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                      title={t('users.page.configureBot')}
                    >
                      <Bot className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(user)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                      title={t('users.page.editUser')}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {user.id !== currentUser?.id && (
                      <button
                        onClick={() => setShowDeleteConfirm(user.id)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-red-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title={t('users.page.deleteUser')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
           </div>{/* End overflow-x-auto wrapper */}
        </div>
        ) : (
          <div className="hidden sm:flex flex-col items-center justify-center py-16 text-center">
            <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              {t('users.noUsersFound')}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('users.addFirstUserSubtitle')}
            </p>
            <button
              onClick={() => { setEditingUser(null); resetForm(); setShowModal(true); }}
              className="px-4 py-2 min-h-[44px] bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('users.addFirstUser')}
            </button>
          </div>
        )}

      {/* Add/Edit User Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingUser ? t('users.page.editUser') : t('users.page.addUser')}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('users.page.firstName')}
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
            <Input
              label={t('users.page.lastName')}
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>
          <Input
            label={t('common.email')}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Input
            label={editingUser ? t('users.newPassword') : t('common.password')}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required={!editingUser}
          />
          
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4 mt-4 space-y-4">
            <h4 className="font-bold text-sm text-gray-900 dark:text-gray-100 uppercase tracking-wider">{t('users.kioskScheduleSection')}</h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('users.page.kioskPin')}
                placeholder={t('users.page.kioskPinPlaceholder')}
                value={formData.kioskPin}
                onChange={(e) => setFormData({ ...formData, kioskPin: e.target.value })}
                maxLength={6}
              />
              
              <div className="flex gap-2 items-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const pin = Math.floor(1000 + Math.random() * 9000).toString();
                    setFormData({ ...formData, kioskPin: pin });
                  }}
                  className="w-full h-[42px] min-h-[42px]"
                >
                  {t('users.generatePin')}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {formData.workShifts.map((shift, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                  <Input
                    label={t('users.page.shiftStart', { number: index + 1 })}
                    type="time"
                    value={shift.startTime}
                    onChange={(e) => {
                      const newShifts = [...formData.workShifts];
                      newShifts[index] = { ...shift, startTime: e.target.value };
                      setFormData({ ...formData, workShifts: newShifts });
                    }}
                  />
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input
                        label={t('users.page.shiftEnd', { number: index + 1 })}
                        type="time"
                        value={shift.endTime}
                        onChange={(e) => {
                          const newShifts = [...formData.workShifts];
                          newShifts[index] = { ...shift, endTime: e.target.value };
                          setFormData({ ...formData, workShifts: newShifts });
                        }}
                      />
                    </div>
                    {formData.workShifts.length > 1 && (
                      <button
                        type="button"
                        className="mb-1 p-2 text-gray-500 hover:text-red-500"
                        onClick={() => {
                          const newShifts = formData.workShifts.filter((_, i) => i !== index);
                          setFormData({ ...formData, workShifts: newShifts });
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700"
                onClick={() => setFormData({
                  ...formData,
                  workShifts: [...formData.workShifts, { startTime: '09:00', endTime: '17:00' }]
                })}
              >
                <Plus className="w-4 h-4" />
                {t('users.page.addShift')}
              </button>
            </div>

            <WeeklyDaySelector
              value={formData.workScheduleDays}
              onChange={(days) => setFormData({ ...formData, workScheduleDays: days })}
              label={t('users.page.workDays')}
              showIcon={false}
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isLoading} className="w-full sm:w-auto">
              {editingUser ? t('users.page.saveChanges') : t('users.page.createUser')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete User Confirmation Modal */}
      <Modal isOpen={!!showDeleteConfirm} onClose={() => setShowDeleteConfirm(null)} title={t('users.page.deleteUser')} size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('users.deleteConfirmText')}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={confirmDelete} className="!bg-red-600 hover:!bg-red-700">{t('common.delete')}</Button>
        </div>
      </Modal>

      {/* Telegram Bot Configuration Modal */}
      <Modal
        isOpen={showTelegramModal}
        onClose={() => {
          setShowTelegramModal(false);
          setTelegramBotToken('');
          setLinkMessage(null);
        }}
        title={t('users.page.configureBotTitle', { firstName: selectedUser?.firstName || '', lastName: selectedUser?.lastName || '' })}
        size="md"
      >
        <form onSubmit={handleLinkTelegram} className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4 mb-4">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">{t('users.howToConfigure')}</h4>
            <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
              <li>{t('users.telegramStep1')}</li>
              <li>{t('users.telegramStep2')}</li>
              <li>{t('users.telegramStep3')}</li>
              <li>{t('users.telegramStep4')}</li>
            </ol>
          </div>
          <Input
            label={t('users.page.botToken')}
            placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
            value={telegramBotToken}
            onChange={(e) => setTelegramBotToken(e.target.value)}
            required
          />
          {linkMessage && (
            <div className={`p-3 rounded-lg text-sm ${
              linkMessage.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              {linkMessage.text}
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => setShowTelegramModal(false)} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isLinkingBot} leftIcon={<Bot className="w-4 h-4" />} className="w-full sm:w-auto">
              {t('users.page.configureBot')}
            </Button>
          </div>
        </form>
      </Modal>
      </main>
    </div>
  );
};

export default UsersPage;
